import React, { useState, useEffect, useRef, useMemo } from "react";
import EnhancedPipelineTable, { EnhancedPipelineTableRef } from "@/components/PipelineTable/EnhancedPipelineTable";
import LeadDetailsSidebar from "@/components/LeadDetails/LeadDetailsSidebar";
import { Lead } from "@/types/leads/Table";
import PipelineTour, { isTourCompleted } from '@/components/Tour/PipelineTour';
import TourButton from '@/components/Tour/TourButton';
import { useUsers } from '@/contexts/UsersContext';

interface UserOption {
  value: string;
  label: string;
  email?: string;
  role?: string;
}

const Pipeline: React.FC = () => {
  const { activeUsers } = useUsers(); // Use shared context
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [runTour, setRunTour] = useState(false);
  const [showTourBadge, setShowTourBadge] = useState(false);

  // Ref to access EnhancedPipelineTable methods
  const pipelineTableRef = useRef<EnhancedPipelineTableRef>(null);

  // Transform active users from context to dropdown format
  const activeUsersForDropdown: UserOption[] = useMemo(() => {
    return activeUsers.map((u) => ({
      value: u.uuid,
      label: u.userName,
      email: u.userEmail,
      role: u.role?.role_name
    }));
  }, [activeUsers]);

  // Show tour badge (tour only starts on-demand via button)
  useEffect(() => {
    const tourCompleted = isTourCompleted();

    // Show badge if tour hasn't been completed (but don't auto-start)
    if (!tourCompleted) {
      setShowTourBadge(true);
    }
  }, []);

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead);
    setIsLeadModalOpen(true);
  };

  const closeLeadModal = () => {
    setIsLeadModalOpen(false);
    setSelectedLead(null);
  };

  const handleLeadUpdate = async () => {
    await pipelineTableRef.current?.refreshPipeline();
  };

  const handleStartTour = () => {
    setRunTour(true);
  };

  const handleTourEnd = () => {
    setRunTour(false);
    setShowTourBadge(false);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        <EnhancedPipelineTable
          ref={pipelineTableRef}
          onLeadClick={handleLeadClick}
          tourButton={<TourButton onClick={handleStartTour} showBadge={showTourBadge} />}
        />
      </div>

      <LeadDetailsSidebar
        isOpen={isLeadModalOpen}
        onClose={closeLeadModal}
        lead={selectedLead}
        activeUsersForDropdown={activeUsersForDropdown}
        onUpdate={handleLeadUpdate}
      />

      <PipelineTour runTour={runTour} onTourEnd={handleTourEnd} />
    </div>
  );
};

export default Pipeline;
